// One-Click Lease - 3 clicks and done!
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  CircularProgress
} from "@mui/material";
import {
  Home,
  Person,
  Description,
  CheckCircle,
  ArrowForward,
  Download,
  Send
} from "@mui/icons-material";
import { propertyService, tenantService, leaseService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";

const steps = ['Select Property', 'Select Tenant', 'Generate Lease'];

function OneClickLease() {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [leaseType, setLeaseType] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [generatedLease, setGeneratedLease] = useState(null);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);

  // Fetch properties and tenants from Firestore on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const [props, tens] = await Promise.all([
          propertyService.getAll(user.uid),
          tenantService.getAll(user.uid),
        ]);
        setProperties(props);
        setTenants(tens);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    fetchData();
  }, [user]);

  const handleNext = () => {
    if (activeStep === 2) {
      generateLease();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedProperty('');
    setSelectedTenant('');
    setLeaseType('standard');
    setGeneratedLease(null);
  };

  const generateLease = async () => {
    setLoading(true);
    try {
      const property = properties.find(p => p.id === selectedProperty);
      const tenant = tenants.find(t => t.id === selectedTenant);

      if (!property || !tenant) {
        console.error("Property or tenant not found");
        return;
      }

      const now = new Date();
      const startDate = now.toISOString().split("T")[0];
      const endDate = leaseType === "standard"
        ? new Date(now.setFullYear(now.getFullYear() + 1)).toISOString().split("T")[0]
        : null;

      const leaseData = {
        propertyId: selectedProperty,
        tenantId: selectedTenant,
        leaseType,
        monthlyRent: property.monthly_rent,
        startDate,
        endDate,
        status: "active",
        propertyAddress: property.address,
        tenantName: `${tenant.first_name} ${tenant.last_name}`,
      };

      const created = await leaseService.create(leaseData, user.uid);

      setGeneratedLease({
        id: created.id,
        summary: {
          property: property.address,
          tenant: `${tenant.first_name} ${tenant.last_name}`,
          rent: property.monthly_rent,
          start_date: startDate,
          type: leaseType === "standard" ? "12 Month Lease" : "Month-to-Month",
        },
      });
    } catch (error) {
      console.error('Error generating lease:', error);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedProperty !== '';
      case 1:
        return selectedTenant !== '';
      case 2:
        return true;
      default:
        return false;
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Home sx={{ mr: 1 }} /> Choose Property
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select a property</InputLabel>
              <Select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                label="Select a property"
              >
                {properties.map((property) => (
                  <MenuItem key={property.id} value={property.id}>
                    {property.address} - ${property.monthly_rent}/mo
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} /> Choose Tenant
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select a tenant</InputLabel>
              <Select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                label="Select a tenant"
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.first_name} {tenant.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Description sx={{ mr: 1 }} /> Lease Type
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select lease type</InputLabel>
              <Select
                value={leaseType}
                onChange={(e) => setLeaseType(e.target.value)}
                label="Select lease type"
              >
                <MenuItem value="standard">Standard (12 months)</MenuItem>
                <MenuItem value="month_to_month">Month-to-Month</MenuItem>
              </Select>
            </FormControl>

            {/* Preview */}
            <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Lease Preview
              </Typography>
              <Typography variant="body2">
                Property: {properties.find(p => p.id === selectedProperty)?.address || 'N/A'}
              </Typography>
              <Typography variant="body2">
                Tenant: {tenants.find(t => t.id === selectedTenant)?.first_name || 'N/A'} {tenants.find(t => t.id === selectedTenant)?.last_name || ''}
              </Typography>
              <Typography variant="body2">
                Type: {leaseType === 'standard' ? '12 Month Lease' : 'Month-to-Month'}
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  if (generatedLease) {
    return (
      <Card sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Lease Generated Successfully!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your lease is ready to send to the tenant
          </Typography>

          <Paper sx={{ p: 3, my: 3, textAlign: 'left', bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              Lease Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" gutterBottom>
              <strong>Property:</strong> {generatedLease.summary.property}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Tenant:</strong> {generatedLease.summary.tenant}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Rent:</strong> ${generatedLease.summary.rent}/month
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Start Date:</strong> {generatedLease.summary.start_date}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Type:</strong> {generatedLease.summary.type}
            </Typography>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Send />}
              size="large"
              onClick={() => alert('Sending lease to tenant...')}
            >
              Send to Tenant
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              size="large"
              onClick={() => alert('Downloading lease...')}
            >
              Download PDF
            </Button>
          </Box>

          <Button onClick={handleReset} sx={{ mt: 3 }}>
            Create Another Lease
          </Button>
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom align="center">
        One-Click Lease Generator
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
        Just 3 clicks to a complete lease agreement
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 200 }}>
        {getStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!canProceed() || loading}
          endIcon={activeStep === 2 ? null : <ArrowForward />}
          sx={{
            background: activeStep === 2
              ? 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)'
              : undefined
          }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : activeStep === 2 ? (
            'Generate Lease'
          ) : (
            'Next'
          )}
        </Button>
      </Box>
    </Card>
  );
}

export default OneClickLease;
